import type JSZipType from "jszip"
import type { CellObject, WorkBook } from "xlsx"

// Heavy, structured formats are kept out of the initial chat bundle. Each
// reader is imported only after a matching file is attached.

export const MAX_STRUCTURED_BYTES = 20 * 1024 * 1024
export const MAX_SPREADSHEET_CELLS = 100_000
export const MAX_PDF_PAGES = 500
export const MAX_DECOMPRESSED_BYTES = 100 * 1024 * 1024

const CONTROL_CHARS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g
const ENTITY = /&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|(amp|lt|gt|quot|apos));/g
const NAMED_ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" }

function clean(text: string): string {
  return text
    .replace(CONTROL_CHARS, "")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

function decodeEntities(text: string): string {
  return text.replace(
    ENTITY,
    (match, dec: string | undefined, hex: string | undefined, name: string | undefined) => {
      if (name) return NAMED_ENTITIES[name] ?? match
      const code = dec ? Number.parseInt(dec, 10) : Number.parseInt(hex ?? "", 16)
      if (!Number.isFinite(code) || code === 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) {
        return code === 0 ? "" : "\ufffd"
      }
      return String.fromCodePoint(code)
    },
  )
}

function stripXml(xml: string): string {
  return decodeEntities(
    xml
      .replace(/<text:(?:tab|line-break)\b[^>]*\/>/g, (tag) => (tag.startsWith("<text:tab") ? "\t" : "\n"))
      .replace(/<[^>]+>/g, ""),
  )
}

function oneLine(value: unknown): string {
  return String(value ?? "")
    .replace(/\t/g, " ")
    .replace(/\r?\n/g, " ")
    .trim()
}

/** Reads the ZIP central directory without inflating anything. */
export function guardZipDecompression(bytes: Uint8Array, name: string): void {
  if (bytes[0] !== 0x50 || bytes[1] !== 0x4b) return
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let end = -1
  for (let offset = bytes.length - 22; offset >= Math.max(0, bytes.length - 65_557); offset--) {
    if (view.getUint32(offset, true) === 0x06054b50) {
      end = offset
      break
    }
  }
  if (end < 0) throw new Error(`${name} has a malformed or incomplete package.`)
  const entries = view.getUint16(end + 10, true)
  let offset = view.getUint32(end + 16, true)
  if (entries === 0xffff || offset === 0xffffffff) {
    throw new Error(`${name} uses ZIP64 packaging, which is outside the safe attachment limits.`)
  }
  let total = 0
  for (let index = 0; index < entries; index++) {
    if (offset + 46 > bytes.length || view.getUint32(offset, true) !== 0x02014b50) {
      throw new Error(`${name} has a malformed package directory.`)
    }
    const size = view.getUint32(offset + 24, true)
    if (size === 0xffffffff) throw new Error(`${name} contains an oversized ZIP64 entry.`)
    total += size
    if (total > MAX_DECOMPRESSED_BYTES) {
      throw new Error(`${name} expands beyond the ${MAX_DECOMPRESSED_BYTES / 1024 / 1024} MiB safety limit.`)
    }
    offset +=
      46 +
      view.getUint16(offset + 28, true) +
      view.getUint16(offset + 30, true) +
      view.getUint16(offset + 32, true)
  }
}

async function openZip(bytes: Uint8Array, name: string): Promise<JSZipType> {
  guardZipDecompression(bytes, name)
  const { default: JSZip } = await import("jszip")
  try {
    return await JSZip.loadAsync(bytes)
  } catch {
    throw new Error(`${name} could not be opened — it may be password-protected, damaged, or incomplete.`)
  }
}

export async function readSpreadsheet(bytes: Uint8Array, name: string): Promise<string> {
  const isOle = bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0
  if (isOle) {
    if (/\.(?:xlsx|xlsm|xltx|xltm|xlsb)$/i.test(name)) {
      throw new Error(`${name} is password-protected, so its cells cannot be read here.`)
    }
    throw new Error(`${name} uses the old binary Excel format. Save it as .xlsx and attach that copy.`)
  }
  guardZipDecompression(bytes, name)
  const XLSX = await import("xlsx")
  let workbook: WorkBook
  try {
    workbook = XLSX.read(bytes, {
      type: "array",
      cellDates: true,
      cellFormula: true,
      cellText: true,
      dense: false,
    })
  } catch (error) {
    const message = error instanceof Error ? error.message.toLowerCase() : ""
    if (message.includes("password") || message.includes("encrypt")) {
      throw new Error(`${name} is password-protected, so its cells cannot be read here.`)
    }
    throw new Error(`${name} could not be opened as a spreadsheet — it may be damaged or incomplete.`)
  }

  let populated = 0
  const sections: string[] = []
  const metadata = new Map(
    (workbook.Workbook?.Sheets ?? []).map((sheet) => [sheet.name, sheet.Hidden ?? 0]),
  )

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName]
    if (!sheet) continue

    const cells = Object.entries(sheet).filter(([address]) => !address.startsWith("!")) as Array<
      [string, CellObject]
    >
    const rows = new Map<number, Array<{ column: number; address: string; value: string }>>()
    for (const [address, cell] of cells) {
      const position = XLSX.utils.decode_cell(address)
      const displayed = oneLine(cell.w ?? cell.v)
      const formula = typeof cell.f === "string" ? `=${cell.f}` : ""
      const value = formula && displayed ? `${formula} => ${displayed}` : formula || displayed
      if (!value) continue
      populated++
      if (populated > MAX_SPREADSHEET_CELLS) {
        throw new Error(
          `${name} contains more than ${MAX_SPREADSHEET_CELLS.toLocaleString()} populated cells. ` +
            "Attach a smaller workbook or export the relevant sheets as CSV.",
        )
      }
      const row = rows.get(position.r) ?? []
      row.push({ column: position.c, address, value })
      rows.set(position.r, row)
    }

    const hidden = metadata.get(sheetName)
    const suffix = hidden === 2 ? " (very hidden)" : hidden === 1 ? " (hidden)" : ""
    const lines = [`## Sheet: ${sheetName}${suffix}`]
    for (const [rowIndex, row] of [...rows.entries()].sort((a, b) => a[0] - b[0])) {
      row.sort((a, b) => a.column - b.column)
      const last = row.at(-1)?.column ?? 0
      if (last <= 255) {
        // Start at column A even when the row's first value is farther right.
        // Otherwise `C1` would be emitted as the first TSV field and its
        // position — often meaningful in a spreadsheet — would silently move.
        const values = Array.from({ length: last + 1 }, () => "")
        for (const cell of row) values[cell.column] = cell.value
        lines.push(`${rowIndex + 1}\t${values.join("\t")}`)
      } else {
        // A malicious !ref, or one accidental value in XFD, must not allocate a
        // 16,384-column row just to preserve empty cells.
        lines.push(`${rowIndex + 1}\t${row.map((cell) => `[${cell.address}] ${cell.value}`).join("\t")}`)
      }
    }
    if (rows.size === 0) lines.push("(empty sheet)")
    sections.push(lines.join("\n"))
  }

  if (sections.length === 0 || populated === 0) {
    throw new Error(`${name} contains no populated cells that can be read.`)
  }
  return clean(sections.join("\n\n"))
}

export async function readOpenDocument(
  bytes: Uint8Array,
  name: string,
  format: "odt" | "odp",
): Promise<string> {
  const zip = await openZip(bytes, name)
  const manifest = await zip.file("META-INF/manifest.xml")?.async("string")
  if (manifest && /<[\w.-]*:?encryption-data\b/.test(manifest)) {
    throw new Error(`${name} is password-protected, so its text cannot be read here.`)
  }
  const entry = zip.file("content.xml")
  if (!entry) throw new Error(`${name} opened, but content.xml is missing; the file is probably damaged.`)
  const xml = await entry.async("string")

  if (format === "odp") {
    const pages = [...xml.matchAll(/<draw:page\b([^>]*)>([\s\S]*?)<\/draw:page>/g)]
    const sections = pages.map((match, index) => {
      const attributes = match[1] ?? ""
      const body = match[2] ?? ""
      const title = decodeEntities(/\bdraw:name="([^"]*)"/.exec(attributes)?.[1] ?? "").trim()
      const noteBlocks = [...body.matchAll(/<presentation:notes\b[^>]*>([\s\S]*?)<\/presentation:notes>/g)]
      const notes = clean(noteBlocks.map((note) => openDocumentFlow(note[1] ?? "")).filter(Boolean).join("\n"))
      const slide = openDocumentFlow(body.replace(/<presentation:notes\b[^>]*>[\s\S]*?<\/presentation:notes>/g, ""))
      return [
        `## Slide ${index + 1}${title ? `: ${title}` : ""}`,
        slide || "(no text on this slide)",
        notes ? `### Notes\n${notes}` : "",
      ].filter(Boolean).join("\n")
    })
    const text = clean(sections.join("\n\n"))
    if (!text || !sections.some((section) => !section.endsWith("(no text on this slide)"))) {
      throw new Error(`${name} contains no text that can be read. Attach screenshots of the relevant slides instead.`)
    }
    return text
  }

  const text = openDocumentFlow(xml)
  if (!text) throw new Error(`${name} contains no text that can be read.`)
  return text
}

/** Preserve paragraph order and table rows without retaining XML formatting. */
function openDocumentFlow(xml: string): string {
  const body = xml
    .replace(/<table:table-cell\b[^>]*>/g, "")
    .replace(/<\/table:table-cell>/g, "\t")
    .replace(/<\/table:table-row>/g, "\n")
    .replace(/<text:(?:p|h)\b[^>]*>/g, "")
    .replace(/<\/text:(?:p|h)>/g, "\n")
  return clean(stripXml(body).replace(/\n\t/g, "\t").replace(/\t\n/g, "\n"))
}

export async function readPdf(bytes: Uint8Array, name: string): Promise<string> {
  const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs")
  if (typeof window !== "undefined") {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
      import.meta.url,
    ).toString()
  }

  const loadingTask = pdfjs.getDocument({ data: bytes })
  let document: Awaited<typeof loadingTask.promise>
  try {
    document = await loadingTask.promise
  } catch (error) {
    const record = error as { name?: string; message?: string }
    if (record.name === "PasswordException" || record.message?.toLowerCase().includes("password")) {
      throw new Error(`${name} is password-protected, so its text cannot be read here.`)
    }
    throw new Error(`${name} could not be opened as a PDF — it may be damaged or incomplete.`)
  }

  try {
    if (document.numPages > MAX_PDF_PAGES) {
      throw new Error(
        `${name} has ${document.numPages.toLocaleString()} pages; the limit is ${MAX_PDF_PAGES.toLocaleString()}. ` +
          "Attach the relevant pages as a smaller PDF.",
      )
    }

    const sections: string[] = []
    let readable = 0
    for (let number = 1; number <= document.numPages; number++) {
      const page = await document.getPage(number)
      const content = await page.getTextContent()
      const chunks: string[] = []
      for (const item of content.items) {
        if (!("str" in item)) continue
        chunks.push(item.str)
        chunks.push(item.hasEOL ? "\n" : " ")
      }
      const text = clean(chunks.join(""))
      if (text) readable++
      sections.push(`## Page ${number}\n${text || "(no text on this page)"}`)
      page.cleanup()
    }

    if (readable === 0) {
      throw new Error(
        `${name} contains no selectable text. It may be a scanned document; attach screenshots of the relevant pages instead.`,
      )
    }
    return sections.join("\n\n")
  } finally {
    await loadingTask.destroy()
  }
}
